import { render } from '@testing-library/react';
import DatePicker, { IDatePickerValue } from 'AppointmentBox/TimeTab/DatePicker';
import Time from 'AppointmentBox/TimeTab/Time';
import { Avatar, Row, Spin } from 'antd';
import { toneChip, toneRing, useStaffGroupTone } from './useStaffGroupTone';
import { AxiosResponse } from 'axios';
import Box from 'components/Box';
import Modal, { IModalProps } from 'components/Modal';
import Text from 'components/Text';
import { TIME_PICKER_INDEX } from 'constants/variables';
import moment from 'utils/moment';
import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useRef, useState } from 'react';
import apis from 'services/apis';
import shopSelectors from 'services/selectors/shop';
import styled from 'styled-components';
import { FORMAT_TIME } from 'utils/format';
import storage from 'utils/sessionStorage';
type Props = {
  renderHeader?: IModalProps['renderHeader'];
  zIndex?: number;
  onChoose?: (o: IDatePickerValue) => void;
  newTitle?: boolean;
  renderTitle?: React.ReactNode;
  renderFooter?: (props: { onCancel: IModalProps['onCancel'] }) => React.ReactNode;
};
type TimeSlotAvailablePickerRef = {
  open: (staffId: string, value: IDatePickerValue) => void;
  close: () => void;
  get: (staffId: string, value: IDatePickerValue) => Promise<IDatePickerValue>;
  fetchData: (staffId: string, value?: IDatePickerValue) => Promise<void>;
};
export const timeSlotAvailablePickerRef = React.createRef<TimeSlotAvailablePickerRef>();
export const useTimeSlotAvailablePickerRef = () => useRef<TimeSlotAvailablePickerRef>(null);
const TimeSlotAvailablePickerUI = forwardRef<TimeSlotAvailablePickerRef, Props>(({ renderHeader, zIndex, onChoose, newTitle, renderTitle, renderFooter }, ref) => {
  const [staffId, setStaffId] = useState<string>('');
  const [open, setOpen] = useState(false);
  const resolveCallback = useRef<(o: IDatePickerValue) => void>(() => undefined);

  const [activeDate, setActiveDate] = useState<IDatePickerValue>(moment());
  const [loading, setLoading] = useState<boolean>(false);
  const [slots, setSlots] = useState<string[]>([]);
  const staffs = shopSelectors.pureStaffs();
  const activeStaff = useMemo(() => staffs.find(o => o.id?.toString() === staffId), [staffs, staffId]);

  const onSetActiveDate = (value: IDatePickerValue) => {
    setActiveDate(value);
    fetchData(staffId, value);
  };

  const selected = activeDate?.format(FORMAT_TIME);

  const handleClose: TimeSlotAvailablePickerRef['close'] = () => {
    setOpen(false);
  };

  const fetchData = async (_staffId: string, _activeDate: IDatePickerValue) => {
    setLoading(true);
    try {
      const res: AxiosResponse<{ payload: { timeSlots: string[] } }> = await apis.getTimeSlot(storage.shopId.get(), _activeDate?.format('YYYY-MM-DD') || '', _staffId || '');
      const timeSlots = res?.data?.payload?.timeSlots;
      if (timeSlots) setSlots(timeSlots);
    } catch (error) { }
    finally {
      setLoading(false);
    }
  };

  const handleGet: TimeSlotAvailablePickerRef['get'] = async (_staffId, _activeDate) => {
    fetchData(_staffId, _activeDate);
    setActiveDate(_activeDate);
    setStaffId(_staffId);
    setOpen(true);
    return new Promise<IDatePickerValue>((resolve) => {
      resolveCallback.current = resolve;
    });
  };

  const handleFetchData: TimeSlotAvailablePickerRef['fetchData'] = async (_staffId, _activeDate) => {
    fetchData(_staffId, _activeDate || activeDate);
    if (_activeDate) {
      setActiveDate(_activeDate);
    }
    setStaffId(_staffId);
  };

  const handleOpen: TimeSlotAvailablePickerRef['open'] = (_staffId, _activeDate) => {
    fetchData(_staffId, _activeDate);
    setActiveDate(_activeDate);
    setStaffId(_staffId);
    setOpen(true);
  };

  const handleChooseDate = (timeStr: string) => () => {
    const time = moment(timeStr, FORMAT_TIME);
    const newDateValue = activeDate?.clone().set({
      hour: time.get('hour'),
      minute: time.get('minute'),
      second: time.get('second'),
    });
    onChoose?.(newDateValue || null);
    resolveCallback.current(newDateValue || null);
    setOpen(false);
  };

  const TimeSlots = useCallback(() => {
    if (loading) return <Row align={'middle'} justify={'center'} style={{ flex: 1 }}><Spin spinning /></Row>;
    return (
      <>
        {slots.map(o => (
          <Time key={o} data={o}
            onClick={handleChooseDate(o)} />
        ))}
      </>
    );
  }, [slots, loading]);

  const toneOf = useStaffGroupTone();
  const Title = useMemo(() => {

    if (!activeStaff) return 'Choose Date and Time';

    return (
      <Box display='flex' alignItems='center'>
        <Text style={{ marginRight: '8px', fontSize: '20px' }} variant="CONTENT_2" color="text_3">
          SELECT TIME FOR STAFF:
        </Text>
        <Avatar style={{ zoom: 1.3, ...toneRing(toneOf(activeStaff.id)) }} src={activeStaff.avatar} >{activeStaff.name?.[0] || ''}</Avatar>
        <Text style={{ marginLeft: '8px', fontSize: '18px', textTransform: 'uppercase' }} variant="CONTENT_2" color="text_3">
          <span style={toneChip(toneOf(activeStaff.id))}>{activeStaff.name}</span>
        </Text>
      </Box>
    );
  }, [activeStaff, toneOf]);

  useImperativeHandle(ref, () => ({
    get: handleGet,
    fetchData: handleFetchData,
    open: handleOpen,
    close: handleClose,
  }));

  return (
    <>
      <Modal
        open={open}
        title={newTitle ? undefined : Title}
        renderHeader={renderHeader}
        visibleOk={false}
        width='95vw'
        closeRight
        zIndex={zIndex || TIME_PICKER_INDEX}
        onCancel={() => setOpen(false)}>
        {!!newTitle && <Text variant='LABEL_SECTION' style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', textAlign: 'center', fontSize: 24, marginBottom: '-8px' }}>{renderTitle}</Text>}
        <TimeTabStyled>
          <Text variant='LABEL_SECTION' >DATE: </Text>
          <DatePicker
            value={activeDate}
            onChange={onSetActiveDate}
          />
          {selected && <>
            <Text  mt={1} variant='LABEL_SECTION' >YOUR TIME: </Text>
            <Times>
              <Time data={selected} active />
            </Times>
          </>}
          <Text mb={1} />
          <Times>
            <TimeSlots />
          </Times>
        </TimeTabStyled>
        {renderFooter && renderFooter({ onCancel: () => setOpen(false) })}
      </Modal>
    </>
  );
});
TimeSlotAvailablePickerUI.displayName = 'TimeSlotAvailablePickerUI';
export { TimeSlotAvailablePickerUI };

const TimeSlotAvailablePicker = {
  get: (staffId: string, activeDate: IDatePickerValue) => timeSlotAvailablePickerRef.current?.get(staffId, activeDate),
};

export default TimeSlotAvailablePicker;


const TimeTabStyled = styled.div`
  height: 100%;
  padding: 1.5rem;
  flex-grow: 1;
`;

const Times = styled.div`
display: grid;
grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
gap: 8px;
margin-top: 12px;
flex-grow: 1;
zoom: 0.9;
`;

